import { useLazyUpdateState } from "@buzzeasy/shared-frontend-utilities";
import { PropsWithChildren, ReactElement, useCallback, useContext, useEffect, useEffectEvent } from "react";
import { groupByProperty } from "../../../utils/arrayUtils";
import aggregateAgentsIntoTeam from "../../calculations/aggregateAgentsIntoTeam";
import { defaultAgentReport, processAgentLiveData, processAgentTodayData, processWorkItemData, toReportDictionary } from "../../calculations/teamCalculations";
import { ReportDictionary } from "../../models/reports";
import { AgentReport, RawAgentLiveData, RawAgentTodayData, RawWorkItemData, TeamReport } from "../../models/reports.agents";
import { Team } from "../../models/team";
import { SignalRConnectionContext } from "./SignalRConnectionContext";
import { TeamReportsContext } from "./TeamReportsContext";

function get(dictionary: ReportDictionary<AgentReport>, id: string): AgentReport {
  return dictionary[id] ?? defaultAgentReport(id);
}

export default function TeamReportsProvider({ children }: PropsWithChildren): ReactElement {
  const { subscribe, unsubscribe, teams } = useContext(SignalRConnectionContext);

  const [agentsDict, setAgentsDict] = useLazyUpdateState<ReportDictionary<AgentReport>>({});

  const receiveTodaysAgentData = useCallback(
    (data: RawAgentTodayData) => setAgentsDict((dict) => {
      const agent = get(dict, data.id);
      const processed = processAgentTodayData(data);
      const merged: AgentReport = { ...agent, ...processed };

      return { ...dict, [merged.id]: merged };
    }),
    [setAgentsDict],
  );

  const receiveLiveAgentData = useCallback(
    (data: RawAgentLiveData[]) => setAgentsDict((dict) => {
      const updatedReports = data.map((a) => {
        const agent = get(dict, a.agentId);
        const processed = processAgentLiveData(a);
        return { ...agent, ...processed };
      });

      const updatedReportsDict = toReportDictionary(updatedReports);
      return { ...dict, ...updatedReportsDict };
    }),
    [setAgentsDict],
  );

  const receiveWorkItemData = useCallback(
    (data: RawWorkItemData[]) => {
      const groupedWorkItems = groupByProperty(data, wi => wi.agentId);
      const processedGroups = Object.map(groupedWorkItems, ([key, wIs]) => [key, wIs.map(processWorkItemData)]);

      setAgentsDict(dict => Object.map(dict, ([key, a]) => [key, { ...a, currentlyHandledWorkItems: processedGroups[a.id] ?? [] }]));
    },
    [setAgentsDict],
  );

  useEffect(
    () => {
      const subId = subscribe({ receiveTodaysAgentData, receiveLiveAgentData, receiveWorkItemData });
      return () => unsubscribe(subId);
    },
    [receiveLiveAgentData, receiveTodaysAgentData, receiveWorkItemData, subscribe, unsubscribe],
  );

  const [teamReports, setTeamReports] = useLazyUpdateState<TeamReport[]>([]);

  const updateTeamReports = useEffectEvent((teams: Team[]) => {
    setTeamReports(
      teams.map(t =>
        aggregateAgentsIntoTeam(t, Object.values(agentsDict)),
      ),
    );
  });

  useEffect(
    () => {
      const interval = setInterval(
        () => updateTeamReports(teams),
        3000,
      );

      return () => clearInterval(interval);
    },
    [teams],
  );

  return (
    <TeamReportsContext.Provider value={teamReports}>
      {children}
    </TeamReportsContext.Provider>
  );
}
